import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Avatar,
  Button,
  ButtonGroup,
  Checkbox,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  Icon,
  IconDotsVertical,
  IconPaperPlaneOutline,
  IconPlusCircular,
  IconTrash,
  IconUserEditPencil,
} from '@/components';
import {
  DataList,
  DataListCell,
  DataListRow,
  DataListTextHeader,
} from '@/components/DataList';
import { Select } from '@/components/Select';
import {
  DepartmentType,
  RoleType,
  type UserBasicDto,
  UserInvitationStatus,
  type UserPositionType,
} from '@/services/api';

import { useGetAccountQuery } from '../auth';
import {
  AddOrEditCompanyUserModal,
  POSITIONS,
  ROLES,
} from './AddNewCandidateModal';
import { UserInterviewsModal } from './UserInterviewsModal';
import {
  useDeleteCompanyUserMutation,
  useEditCompanyUserDepartmentMutation,
  useEditCompanyUserPositionMutation,
  useEditCompanyUserRoleMutation,
  useGetCompanyUsers,
  useResendUserInvitationMutation,
} from './queries';

export const PageAdminAccountTeam: FC = () => {
  const { t } = useTranslation(['common', 'components']);
  const [openPopoverId, setOpenPopoverId] = useState<string>();
  const [showAddCompanyUserModal, setShowAddCompanyUserModal] = useState(false);
  const [showEditCompanyUserModal, setEditAddCompanyUserModal] =
    useState(false);
  const [selectedUserIdForEdit, setSelectedUserIdForEdit] = useState<
    string | undefined
  >(undefined);
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);
  const [showInterviewsModal, setShowInterviewsModal] = useState(false);
  const [selectedUserForInterviews, setSelectedUserForInterviews] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const list = useGetCompanyUsers();

  // Get the current user data from the fresh query result
  const selectedUserForEdit = selectedUserIdForEdit
    ? list.data?.find((user) => user.id === selectedUserIdForEdit)
    : undefined;

  const users = useMemo(() => list.data ?? [], [list.data]);
  const selectAll = users.length > 0 && selectedUsers.length === users.length;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedUsers(users.map((user) => user.id));
      } else {
        setSelectedUsers([]);
      }
    },
    [users]
  );

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  }, []);

  return (
    <VStack rowGap={5}>
      <HStack justify="space-between" align="start" w="full">
        <VStack align="start" rowGap={1}>
          <Text color="gray.900" fontSize="xl" fontWeight="semibold">
            {t('components:settings.account.team.title')}
          </Text>
          <Text color="gray.600">
            {t('components:settings.account.team.subTitle')}
          </Text>
        </VStack>

        <Button
          leftIcon={
            <Icon icon={IconPlusCircular} fontSize="20px" color="#656D75" />
          }
          color="gray.600"
          onClick={() => setShowAddCompanyUserModal(true)}
        >
          {t('common:actions.inviteNewMember')}
        </Button>
      </HStack>

      <DataList borderColor="gray.50" borderRadius="xl" w="100%">
        <AccountTeamListHeader
          listLength={list.data?.length ?? 0}
          selectAll={selectAll}
          handleSelectAll={handleSelectAll}
        />

        {list.isLoading && (
          <HStack justify="center" align="center" w="full" h="100%" mt={9}>
            <Spinner size="xs" />
          </HStack>
        )}

        {/* List Body */}
        {list.data?.map((item) => {
          return (
            <AccountTeamItemRow
              {...item}
              key={item.id}
              user={item}
              openPopoverId={openPopoverId}
              setOpenPopoverId={setOpenPopoverId}
              onEditUser={(user) => {
                setSelectedUserIdForEdit(user.id);
                setEditAddCompanyUserModal(true);
              }}
              onShowInterviews={(user) => {
                setSelectedUserForInterviews({
                  id: user.id,
                  name: `${user.name} ${user.surname}`,
                });
                setShowInterviewsModal(true);
              }}
              selectedUsers={selectedUsers}
              handleUserSelect={handleUserSelect}
            />
          );
        })}
      </DataList>

      <AddOrEditCompanyUserModal
        isOpen={showAddCompanyUserModal}
        onClose={() => setShowAddCompanyUserModal(false)}
      />

      <AddOrEditCompanyUserModal
        isOpen={showEditCompanyUserModal}
        onClose={() => {
          setEditAddCompanyUserModal(false);
          setSelectedUserIdForEdit(undefined);
        }}
        isEdit
        user={selectedUserForEdit}
      />

      <UserInterviewsModal
        isOpen={showInterviewsModal}
        onClose={() => {
          setShowInterviewsModal(false);
          setSelectedUserForInterviews(null);
        }}
        userId={selectedUserForInterviews?.id ?? ''}
        userName={selectedUserForInterviews?.name ?? ''}
      />
    </VStack>
  );
};

type AccountTeamListHeaderProps = {
  listLength: number;
  isShowTeamMemberCount?: boolean;
  selectAll: boolean;
  handleSelectAll: (checked: boolean) => void;
  isShowInterviews?: boolean;
};

export const AccountTeamListHeader: FC<AccountTeamListHeaderProps> = ({
  listLength,
  isShowTeamMemberCount = true,
  selectAll,
  handleSelectAll,
  isShowInterviews = true,
}) => {
  const { t } = useTranslation(['common', 'components']);

  return (
    <>
      {isShowTeamMemberCount && (
        <DataListRow>
          <DataListCell flex={1}>
            <DataListTextHeader>
              <Text color="gray.900" fontSize="xl" fontWeight="semibold">
                {`${t('components:settings.account.team.teamMember')} (${listLength})`}
              </Text>
            </DataListTextHeader>
          </DataListCell>
        </DataListRow>
      )}

      <DataListRow backgroundColor="gray.25">
        <DataListCell
          flex={3}
          flexDirection="row"
          alignItems="center"
          justifyContent="start"
          gap={3}
          minWidth="350px"
        >
          <Checkbox
            isChecked={selectAll}
            onChange={(e) => handleSelectAll?.(e.target.checked)}
          />
          <DataListTextHeader color="gray.600" fontSize="14px" fontWeight="500">
            {t('common:information.personalInformation').toAppLocaleUpperCase()}
          </DataListTextHeader>
        </DataListCell>
        <DataListCell flex={1.5} align="center" minWidth="180px">
          <DataListTextHeader color="gray.600" fontSize="14px" fontWeight="500">
            {t('common:information.systemAuthority').toAppLocaleUpperCase()}
          </DataListTextHeader>
        </DataListCell>
        <DataListCell flex={1.5} align="center" minWidth="180px">
          <DataListTextHeader color="gray.600" fontSize="14px" fontWeight="500">
            {t('common:information.role').toAppLocaleUpperCase()}
          </DataListTextHeader>
        </DataListCell>
        <DataListCell flex={2} align="center" minWidth="180px">
          <DataListTextHeader color="gray.600" fontSize="14px" fontWeight="500">
            {t('components:board.department').toAppLocaleUpperCase()}
          </DataListTextHeader>
        </DataListCell>
        <DataListCell flex={1.5} align="center" minWidth="180px">
          <DataListTextHeader color="gray.600" fontSize="14px" fontWeight="500">
            {t('common:detail.status').toAppLocaleUpperCase()}
          </DataListTextHeader>
        </DataListCell>
        <DataListCell
          flex={1.5}
          align="center"
          minWidth={isShowInterviews ? '250px' : '50px'}
        />
      </DataListRow>
    </>
  );
};

export const AccountTeamItemRow: FC<
  UserBasicDto & {
    openPopoverId?: string;
    setOpenPopoverId?: Dispatch<SetStateAction<string | undefined>>;
    user: UserBasicDto;
    isShowInterviews?: boolean;
    onEditUser?: (user: UserBasicDto) => void;
    onShowInterviews?: (user: UserBasicDto) => void;
    selectedUsers: Array<string>;
    handleUserSelect: (userId: string, checked: boolean) => void;
  }
> = ({
  user,
  openPopoverId,
  setOpenPopoverId,
  isShowInterviews = true,
  onEditUser,
  onShowInterviews,
  selectedUsers,
  handleUserSelect,
}) => {
  const { t } = useTranslation(['components', 'common']);
  const { name, surname, email, role, department, position } = user;
  const [selectedRole, setSelectedRole] = useState(role);
  const [selectedDepartment, setSelectedDepartment] = useState(department);
  const [selectedPosition, setSelectedPosition] = useState(position);

  // Sync local state with user prop changes
  useEffect(() => {
    setSelectedRole(role);
  }, [role]);

  useEffect(() => {
    setSelectedDepartment(department);
  }, [department]);
  const editCompanyUserRoleMutation = useEditCompanyUserRoleMutation();
  const editCompanyUserPositionMutation = useEditCompanyUserPositionMutation();
  const editCompanyUserDepartmentMutation =
    useEditCompanyUserDepartmentMutation();
  const currentUser = useGetAccountQuery();
  const isNotAbleToDelete =
    (user.role === RoleType.SuperAdmin &&
      currentUser.data?.role === RoleType.Administrator) ||
    currentUser.data?.id === user.id;
  const isMyAccount = currentUser.data?.id === user.id;

  const handleRoleChange = useCallback(
    (newRole: RoleType) => {
      editCompanyUserRoleMutation.mutate({
        id: user.id,
        role: newRole,
      });
      setSelectedRole(newRole);
    },
    [editCompanyUserRoleMutation, user.id, setSelectedRole]
  );

  const handlePositionChange = useCallback(
    (position: UserPositionType) => {
      editCompanyUserPositionMutation.mutate({
        id: user.id,
        position: position,
      });
      setSelectedPosition(position);
    },
    [editCompanyUserPositionMutation, user.id, setSelectedPosition]
  );

  const handleDepartmentChange = useCallback(
    (newDepartment: DepartmentType) => {
      editCompanyUserDepartmentMutation.mutate({
        id: user.id,
        department: newDepartment,
      });
      setSelectedDepartment(newDepartment);
    },
    [editCompanyUserDepartmentMutation, user.id, setSelectedDepartment]
  );

  const getInvitationStatusText = (status: string | undefined) => {
    switch (status) {
      case 'Pending':
        return t('components:settings.account.team.pending');
      case 'Accepted':
        return t('components:settings.account.team.accepted');
      case 'Removed':
        return t('components:settings.account.team.removed');
      default:
        return status;
    }
  };

  return (
    <DataListRow bg={user.invitationStatus === 'Removed' ? 'gray.25' : 'white'}>
      <DataListCell
        flex={3}
        flexDirection="row"
        alignItems="center"
        justifyContent="start"
        gap={3}
        minWidth="350px"
      >
        <Checkbox
          isChecked={selectedUsers?.includes(user.id)}
          onChange={(e) => handleUserSelect?.(user.id, e.target.checked)}
        />
        <DataListTextHeader>
          <HStack>
            <Avatar size="md" name={`${name} ${surname}`} />
            <Stack gap={0}>
              <Text color="gray.900" fontSize="16px" fontWeight="500">
                {name} {surname}
              </Text>
              <Text color="gray.500" fontSize="14px" fontWeight="400">
                {email}
              </Text>
            </Stack>
          </HStack>
        </DataListTextHeader>
      </DataListCell>
      <DataListCell flex={1.5} align="center" minWidth="180px">
        <Stack alignItems="center" justifyContent="center" width="100%">
          <Select
            type="select"
            size="sm"
            options={ROLES}
            value={ROLES.find((r) => r.value === selectedRole)}
            onChange={(option) => {
              if (option && 'value' in option) {
                handleRoleChange(option.value as RoleType);
              }
            }}
            isDisabled={
              editCompanyUserRoleMutation.isPending ||
              user.invitationStatus === 'Removed' ||
              isMyAccount
            }
            placeholder={t('common:actions.selectPosition')}
            chakraStyles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
            }}
          />
        </Stack>
      </DataListCell>
      <DataListCell flex={1.5} align="center" minWidth="180px">
        <Stack alignItems="center" justifyContent="center" width="100%">
          <Select
            type="select"
            size="sm"
            options={POSITIONS}
            value={POSITIONS.find((r) => r.value === selectedPosition)}
            onChange={(option) => {
              if (option && 'value' in option) {
                handlePositionChange(option.value as UserPositionType);
              }
            }}
            isDisabled={
              editCompanyUserPositionMutation.isPending ||
              user.invitationStatus === 'Removed' ||
              isMyAccount
            }
            placeholder={t('common:actions.selectRole')}
            chakraStyles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
            }}
          />
        </Stack>
      </DataListCell>
      <DataListCell flex={2} minWidth="180px">
        <Stack alignItems="center" justifyContent="center" width="100%">
          <Select
            type="select"
            size="sm"
            options={DEPARTMENTS}
            value={DEPARTMENTS.find((r) => r.value === selectedDepartment)}
            onChange={(option) => {
              if (option && 'value' in option) {
                handleDepartmentChange(option.value as DepartmentType);
              }
            }}
            isDisabled={
              editCompanyUserDepartmentMutation.isPending ||
              user.invitationStatus === 'Removed' ||
              isMyAccount
            }
            placeholder={t('components:board.department')}
            chakraStyles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
            }}
          />
        </Stack>
      </DataListCell>
      <DataListCell flex={1.5} align="center" minWidth="180px">
        <DataListTextHeader
          color="gray.700"
          px={2}
          py={1}
          borderColor="gray.100"
          bg={
            user.invitationStatus === UserInvitationStatus.Pending
              ? 'gray.50'
              : 'transparent'
          }
          borderWidth={1}
          borderRadius="3xl"
          fontSize="12px"
          fontWeight="semibold"
          textAlign="center"
          wordBreak="keep-all"
        >
          {getInvitationStatusText(user.invitationStatus)}
        </DataListTextHeader>
      </DataListCell>
      <DataListCell
        flex={1.5}
        align="center"
        minWidth={isShowInterviews ? '250px' : '50px'}
      >
        <HStack spacing={2} justify="center" align="center">
          {isShowInterviews && (
            <Button
              variant="link"
              colorScheme="brand"
              fontSize="sm"
              onClick={() => onShowInterviews?.(user)}
            >
              {t('components:layout.interviews')}
            </Button>
          )}
          <ActionMenu
            user={user}
            invitationStatus={user.invitationStatus}
            isNotAbleToDelete={isNotAbleToDelete}
            onEdit={() => onEditUser?.(user)}
            openPopoverId={openPopoverId}
            setOpenPopoverId={setOpenPopoverId}
          />
        </HStack>
      </DataListCell>
    </DataListRow>
  );
};

const ActionMenu: FC<{
  user: UserBasicDto;
  isNotAbleToDelete: boolean;
  onEdit: () => void;
  openPopoverId?: string;
  setOpenPopoverId?: Dispatch<SetStateAction<string | undefined>>;
  invitationStatus?: UserInvitationStatus;
}> = ({
  user,
  isNotAbleToDelete,
  onEdit,
  openPopoverId,
  setOpenPopoverId,
  invitationStatus,
}) => {
  const { t } = useTranslation(['components', 'common', 'auth']);
  const toast = useToast();

  const resendUserInvitationMutation = useResendUserInvitationMutation({
    onSuccess: () => {
      setOpenPopoverId?.(undefined);
      toast({
        title: t('components:settings.account.team.resendSuccess'),
        status: 'success',
      });
    },
  });
  const deleteCompanyUserMutation = useDeleteCompanyUserMutation({
    onSuccess: () => {
      setOpenPopoverId?.(undefined);
      toast({
        title: t('components:settings.account.team.deleteSuccess'),
        status: 'success',
      });
    },
  });

  const onResend = useCallback(() => {
    resendUserInvitationMutation.mutate({
      userId: user.id,
    });
  }, [resendUserInvitationMutation, user.id]);

  const onDelete = useCallback(() => {
    deleteCompanyUserMutation.mutate({
      userId: user.id,
    });
  }, [deleteCompanyUserMutation, user.id]);

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button color="gray.600" variant="link" disabled={isNotAbleToDelete}>
          <IconDotsVertical color="gray.500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        minWidth="160px"
        boxShadow="lg"
        border="1px solid"
        borderColor="gray.200"
      >
        <PopoverBody p={0}>
          <Stack spacing={0}>
            {invitationStatus !== 'Pending' &&
              invitationStatus !== 'Removed' && (
                <Button
                  variant="ghost"
                  bg="none"
                  size="sm"
                  justifyContent="flex-start"
                  leftIcon={<IconUserEditPencil color="gray.500" />}
                  onClick={onEdit}
                  borderRadius={0}
                >
                  {t('common:actions.edit')}
                </Button>
              )}

            {(invitationStatus === 'Pending' ||
              invitationStatus === 'Removed') && (
              <Button
                variant="ghost"
                bg="none"
                size="sm"
                justifyContent="flex-start"
                leftIcon={<IconPaperPlaneOutline color="gray.500" />}
                isLoading={resendUserInvitationMutation.isPending}
                onClick={onResend}
                borderRadius={0}
              >
                {t('auth:otp.resend')}
              </Button>
            )}

            {invitationStatus !== 'Removed' && (
              <Popover
                isOpen={openPopoverId === user.id}
                onOpen={() => {
                  setOpenPopoverId?.(user.id);
                }}
                onClose={() => {
                  setOpenPopoverId?.(undefined);
                }}
                placement="right"
              >
                <PopoverTrigger>
                  <Button
                    variant="ghost"
                    bg="none"
                    size="sm"
                    justifyContent="flex-start"
                    leftIcon={<IconTrash color="red.500" />}
                    color="red.500"
                    disabled={isNotAbleToDelete}
                    borderRadius={0}
                    w="full"
                  >
                    {t('common:actions.delete')}
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <PopoverHeader fontWeight="semibold">
                      {t('components:settings.account.modal.delete.title')}
                    </PopoverHeader>
                    <PopoverBody>
                      {t('components:settings.account.modal.delete.desc')}
                    </PopoverBody>
                    <PopoverFooter display="flex" justifyContent="flex-end">
                      <ButtonGroup size="sm">
                        <Button
                          onClick={() => setOpenPopoverId?.(undefined)}
                          variant="outline"
                        >
                          {t('common:actions.cancel')}
                        </Button>
                        <Button
                          colorScheme="red"
                          isLoading={deleteCompanyUserMutation.isPending}
                          onClick={onDelete}
                        >
                          {t('common:actions.yes')}
                        </Button>
                      </ButtonGroup>
                    </PopoverFooter>
                  </PopoverContent>
                </Portal>
              </Popover>
            )}
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const DEPARTMENTS = [
  {
    label: t('components:board.humanResources'),
    value: DepartmentType.HumanResources,
  },
  {
    label: t('components:board.marketing'),
    value: DepartmentType.Marketing,
  },
  {
    label: t('components:board.sales'),
    value: DepartmentType.Sales,
  },
  {
    label: t('components:board.informationTechnologies'),
    value: DepartmentType.InformationTechnologies,
  },
  {
    label: t('components:board.finance'),
    value: DepartmentType.Finance,
  },
];
